import React, { SFC, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import loadable from '@loadable/component';
import { StyledPlusIcon, StyledMinusIcon } from '../../Generic/Icons/StyledIcons';
import { StyledTextSpan } from '../../Generic/Text/StyledText';
import {
    NewProductFilterValues,
    ProductFilters,
    productFilterTypes,
    pathFilterNames,
    isBrandsFilter,
} from '../../../redux/products/types';
import StateInterface from '../../../redux/types';
import { CategoriesState } from '../../../redux/category/types';
import SkipRender from '../../Generic/SkipRender';
import TextWrapperPadding from '../../Generic/BoxHelpers';
import { getCombinedDisplayValue } from '../../../filterDisplayValueService';
import useConfig from '../../../hooks/useConfig';
import { useFilterState } from '../../../hooks/ListingPage/useFilterState';
import MultiRangeFilters from './MultiRangeFilters';
import StandardFilters from './StandardFilters';
import { FilterValue } from './filterValueService';
import BrandsFilter from './BrandsFilter';
import CategoryFilter from './CategoryFilters';

const RangeFilters = loadable(() => import('./RangeFilters'));

const FiltersGroupContainer = styled.div`
    border-top: 1px solid ${({ theme }) => theme.colors.charcoal20};
    padding: 15px 0;

    :first-child {
        border-top: none;
    }
`;

export const StyledMenuItem = styled.div`
    display: grid;
    grid-template-columns: auto 1fr 10px;
    grid-gap: 4px;
`;

const StyledRestrictedLengthSpan = styled(StyledTextSpan)`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const StyledFilterDescription = styled.div`
    color: ${({ theme }) => theme.colors.turquoise100};
    display: grid;
    grid-template-columns: auto auto auto 1fr;
    text-transform: capitalize;
`;

const StyledFilterContainer = styled.div`
    padding: 7px 0;
    display: flex;
`;

const StyledParentContainer = styled.div`
    padding-top: 10px;
`;

type Props = OwnProps & FromState;

interface OwnProps {
    group: ProductFilters;
    handleValueChange: (value: NewProductFilterValues) => void;
}

interface FromState {
    categories: CategoriesState;
    filters: ProductFilters[];
}

interface FilterContainerProps {
    group: ProductFilters;
    filterValue: FilterValue;
    handleValueChange: (value: NewProductFilterValues) => void;
}

const UnknownFilter: React.SFC<{ productFilter: ProductFilters }> = React.memo(({ productFilter }) => (
    <SkipRender
        message={`Unknown filter type "${productFilter.type}" was tried to be rendered`}
        data={{ group: productFilter }}
    />
));

export const FilterContainer: SFC<FilterContainerProps> = ({
    group,
    handleValueChange,
    filterValue,
}): React.ReactElement => {
    const { name, type, values } = group;

    if (isBrandsFilter(group)) {
        return (
            <BrandsFilter value={filterValue} filterOptions={group} name={name} handleValueChange={handleValueChange} />
        );
    }

    if (name === pathFilterNames.categories) {
        return <CategoryFilter filterValue={filterValue} values={values} handleValueChange={handleValueChange} />;
    }

    switch (type) {
        case productFilterTypes.range:
            return (
                <RangeFilters value={filterValue} type={name} values={values} handleValueChange={handleValueChange} />
            );
        case productFilterTypes.standard:
            return (
                <StandardFilters
                    value={filterValue}
                    filterOptions={values}
                    name={name}
                    handleValueChange={handleValueChange}
                />
            );
        case productFilterTypes.multiRange:
            return (
                <>
                    {Object.keys(values).map(radio => (
                        <StyledFilterContainer key={radio}>
                            <MultiRangeFilters
                                value={filterValue}
                                name={name}
                                radio={radio}
                                handleValueChange={handleValueChange}
                            />
                        </StyledFilterContainer>
                    ))}
                </>
            );
        default:
            return <UnknownFilter productFilter={group} />;
    }
};

interface DescriptionProps {
    children: string;
}

const Description: React.SFC<DescriptionProps> = ({ children }) => (
    <StyledFilterDescription>
        (<StyledRestrictedLengthSpan textType="body2">{children}</StyledRestrictedLengthSpan>)
    </StyledFilterDescription>
);

export const FilterGroup: SFC<Props> = ({ group, handleValueChange, categories, filters }) => {
    useEffect(() => {
        RangeFilters.preload();
    });

    const [showChildren, setShowChildren] = useState(false);
    const { currency } = useConfig();

    const [appliedFilter, onFilterChange] = useFilterState(group);
    const filterValueText = getCombinedDisplayValue(appliedFilter, { categories, filters, currency });

    const onFilterValueChange = (newFilterValue: NewProductFilterValues) => {
        handleValueChange(newFilterValue);
        onFilterChange(newFilterValue);
    };

    if (!Object.values(productFilterTypes).includes(group.type)) {
        return <UnknownFilter productFilter={group} />;
    }

    return (
        <FiltersGroupContainer data-testid={group.name}>
            <StyledMenuItem
                onClick={() => {
                    setShowChildren(!showChildren);
                }}
            >
                <TextWrapperPadding leftPadding={0} rightPadding={10}>
                    <StyledTextSpan textType="body2">{group.displayName}</StyledTextSpan>
                </TextWrapperPadding>
                {filterValueText ? <Description>{filterValueText}</Description> : <div />}
                {!showChildren ? <StyledPlusIcon /> : <StyledMinusIcon />}
            </StyledMenuItem>
            {showChildren && (
                <StyledParentContainer>
                    <FilterContainer
                        filterValue={appliedFilter.values}
                        group={group}
                        handleValueChange={onFilterValueChange}
                    />
                </StyledParentContainer>
            )}
        </FiltersGroupContainer>
    );
};

const mapStateToProps = (state: StateInterface) => ({
    categories: state.categories,
    filters: state.products.filters,
});

export default connect(mapStateToProps)(FilterGroup);
